import { Quiz } from '@renderer/@types/types'
import { useMemo } from 'react'
import { create } from 'zustand'
import { quiz1, quizGroup, quizInnovation, quizTransport, quizSolutionVille, quizRatpDev, quizMetroMusic, quizJEP, quizGroupUMOP } from '@renderer/lib/data/quiz'

const defaultValues = {
  quizzes: [quiz1, quizGroup, quizInnovation, quizTransport, quizSolutionVille, quizRatpDev, quizMetroMusic, quizJEP, quizGroupUMOP],
  currentQuiz: null,
  questions: quiz1,
  currentIndex: 0,
  score: 0,
  currentAnswersIds: [] as number[],
  isCorrectAnswer: null as boolean | null,
  validatedAnswers: false as boolean,
  isQuizEnded: false
}

interface QuizStore {
  quizzes: Quiz[]
  currentQuiz: string | null
  questions: Quiz
  currentIndex: number
  score: number
  currentAnswersIds: number[]
  isCorrectAnswer: boolean | null
  validatedAnswers: boolean
  isQuizEnded: boolean
  setCurrentQuiz: (quiz: string) => void
  setQuestions: (questions: Quiz) => void
  selectAnswer: (answerId: number) => void
  validateAnswers: () => void
  next: () => void
  prev: () => void
  reset: () => void
}

export const useQuizStore = create<QuizStore>((set, get) => ({
  ...defaultValues,
  setCurrentQuiz: (quiz) => {
    if (quiz === 'quiz1') {
      set({ ...defaultValues, currentQuiz: quiz, questions: quiz1 })
    } else if (quiz === 'quizGroup') {
      set({ ...defaultValues, currentQuiz: quiz, questions: quizGroup })
    } else if (quiz === 'quizInnovation') {
      set({ ...defaultValues, currentQuiz: quiz, questions: quizInnovation })
    } else if (quiz === 'quizTransport') {
      set({ ...defaultValues, currentQuiz: quiz, questions: quizTransport })
    } else if (quiz === 'quizSolutionVille') {
      set({ ...defaultValues, currentQuiz: quiz, questions: quizSolutionVille })
    } else if (quiz === 'quizRatpDev') {
      set({ ...defaultValues, currentQuiz: quiz, questions: quizRatpDev })
    } else if (quiz === 'quizMetroMusic') {
      set({ ...defaultValues, currentQuiz: quiz, questions: quizMetroMusic })
    } else if (quiz === 'quizJEP') {
      set({ ...defaultValues, currentQuiz: quiz, questions: quizJEP })
    } else if (quiz === 'quizGroupUMOP') {
      set({ ...defaultValues, currentQuiz: quiz, questions: quizGroupUMOP })
    }
  },
  validateAnswers: () => {
    console.log('validateAnswers')
    set({ validatedAnswers: true })},
  setQuestions: (questions) => set({ questions }),

  selectAnswer: (answerId) => {
    const { questions, currentIndex, currentAnswersIds } = get()
    const currentQuestion = questions[currentIndex]
    if (!currentQuestion) return

    let updatedAnswersIds = [...currentAnswersIds]

    if (currentQuestion.type === 'single') {
      updatedAnswersIds = [answerId]
    } else {
      const answerIndex = updatedAnswersIds.indexOf(answerId)

      if (answerIndex === -1) {
        updatedAnswersIds.push(answerId)
      } else {
        updatedAnswersIds.splice(answerIndex, 1)
      }
    }

    const isCorrect =
      currentQuestion.type === 'single'
        ? answerId === currentQuestion.goodOptionId
        : currentQuestion.type === 'multiple'
          ? updatedAnswersIds.length === currentQuestion.goodOptionsIds.length &&
            updatedAnswersIds.every((id) => currentQuestion.goodOptionsIds.includes(id)) &&
            currentQuestion.goodOptionsIds.every((id) => updatedAnswersIds.includes(id))
          : false

    set({
      currentAnswersIds: updatedAnswersIds,
      isCorrectAnswer: isCorrect,
      score: isCorrect ? get().score + 1 : get().score
    })
  },

  next: () => {
    const { questions, currentIndex } = get()
    if (currentIndex < questions.length - 1) {
      set({
        currentIndex: currentIndex + 1,
        currentAnswersIds: [],
        isCorrectAnswer: null,
        validatedAnswers: false
      })
    }
    else {
      set({
        isQuizEnded: true
      })
    }
  },

  prev: () => {
    const { currentIndex } = get()
    if (currentIndex > 0) {
      set({
        currentIndex: currentIndex - 1,
        currentAnswersIds: [],
        isCorrectAnswer: null,
        validatedAnswers: false
      })
    }
  },

  reset: () => set(defaultValues)
}))

export const useQuiz = () => {
  // const [quiz, setQuiz] = useState<Quiz>(quiz1)
  const quizzes = useQuizStore((s) => s.quizzes)
  const currentIndex = useQuizStore((s) => s.currentIndex)
  const currentQuiz = useQuizStore((s) => s.currentQuiz)
  const questions = useQuizStore((s) => s.questions)
  const score = useQuizStore((s) => s.score)
  const currentAnswersIds = useQuizStore((s) => s.currentAnswersIds)
  const isCorrectAnswer = useQuizStore((s) => s.isCorrectAnswer)
  const validatedAnswers = useQuizStore((s) => s.validatedAnswers)
  const isQuizEnded = useQuizStore((s) => s.isQuizEnded)
  const selectAnswer = useQuizStore((s) => s.selectAnswer)
  const validateAnswers = useQuizStore((s) => s.validateAnswers)
  const next = useQuizStore((s) => s.next)
  const prev = useQuizStore((s) => s.prev)
  const reset = useQuizStore((s) => s.reset)
  const setQuestions = useQuizStore((s) => s.setQuestions)
  const setCurrentQuiz = useQuizStore((s) => s.setCurrentQuiz)

  const currentQuestion = useMemo(() => questions[currentIndex], [questions, currentIndex])
  const totalQuestions = questions.length
  const isAnswered = useMemo(() => currentAnswersIds.length > 0, [currentAnswersIds])
  const isLastQuestion = useMemo(
    () => currentIndex === questions.length - 1,
    [currentIndex, questions]
  )
  return {
    quizzes,
    currentQuiz,
    currentQuestion,
    currentIndex,
    totalQuestions,

    score,
    currentAnswersIds,
    isCorrectAnswer,
    isAnswered,
    isQuizEnded,
    setCurrentQuiz,
    setQuestions,
    selectAnswer,
    next,
    prev,
    reset,
    isLastQuestion,
    validatedAnswers,
    validateAnswers
  }
}
